import { reviewLanguageOverride } from './highlight'
import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, LanguageCode, PSEUDO_LOCALE, toSupportedLanguage } from './languages'

/** An interface language, or the development pseudo-locale. */
export type InterfaceLanguage = LanguageCode | typeof PSEUDO_LOCALE

export const isPseudoAllowed = () => process.env.NODE_ENV === 'development'

interface DetectionSources {
  /** The signed-in learner's `native_language`, when the profile has one. */
  profileLanguage?: unknown
  /** The `maayot.lang` localStorage value. */
  storedLanguage?: unknown
  /** `navigator.languages`, most preferred first. */
  browserLanguages?: readonly unknown[]
  /** A `?lang=` value. Only honoured when pseudo is allowed (development) or `allowQuery` is set. */
  queryLanguage?: unknown
  allowPseudo?: boolean
  /** Honour `?lang=` (a real language only): a page opened from the translation review dashboard. */
  allowQuery?: boolean
}

const asInterfaceLanguage = (value: unknown, allowPseudo: boolean): InterfaceLanguage | null => {
  if (allowPseudo && typeof value === 'string' && value.toLowerCase() === PSEUDO_LOCALE.toLowerCase()) {
    return PSEUDO_LOCALE
  }
  return toSupportedLanguage(value)
}

/**
 * The interface language, in the contract's order: profile `native_language`, then the
 * `maayot.lang` localStorage value, then `navigator.languages`, then English. In development a
 * `?lang=` value (including the `en-XA` pseudo-locale) wins over all of them.
 */
export const detectLanguage = ({
  profileLanguage,
  storedLanguage,
  browserLanguages = [],
  queryLanguage,
  allowPseudo = false,
  allowQuery = false,
}: DetectionSources): InterfaceLanguage => {
  if (allowPseudo || allowQuery) {
    const fromQuery = asInterfaceLanguage(queryLanguage, allowPseudo)
    if (fromQuery) return fromQuery
  }
  const fromProfile = toSupportedLanguage(profileLanguage)
  if (fromProfile) return fromProfile
  const fromStorage = asInterfaceLanguage(storedLanguage, allowPseudo)
  if (fromStorage) return fromStorage
  for (const candidate of browserLanguages) {
    const fromBrowser = toSupportedLanguage(candidate)
    if (fromBrowser) return fromBrowser
  }
  return DEFAULT_LANGUAGE
}

export const readStoredLanguage = (): string | null => {
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  } catch {
    return null
  }
}

export const writeStoredLanguage = (language: string) => {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    /* storage blocked: the language still applies for this visit */
  }
}

const readBrowserLanguages = (): readonly string[] => {
  if (typeof navigator === 'undefined') return []
  if (navigator.languages?.length) return navigator.languages
  return navigator.language ? [navigator.language] : []
}

const readQueryLanguage = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get('lang')
  } catch {
    return null
  }
}

/** The language to start in before the profile has loaded. */
export const detectInitialLanguage = (): InterfaceLanguage =>
  detectLanguage({
    storedLanguage: readStoredLanguage(),
    browserLanguages: readBrowserLanguages(),
    queryLanguage: readQueryLanguage(),
    allowPseudo: isPseudoAllowed(),
    allowQuery: reviewLanguageOverride(),
  })
